/**
 * Smart Ultra-HD Client-Side Image Optimizer (2K 2048px, 92% Visually Lossless Quality)
 * - Keeps 100% crystal-clear HD visual quality (high-bicubic smoothing, 2048px max dimension, 0.92 quality).
 * - Skips files already <= 1 MB or vector/animated formats (SVG, GIF).
 * - Safe fallback: if the browser cannot decode a format, returns the original File untouched.
 */

const DEFAULT_MAX_DIMENSION = 2048; // 2K Ultra-HD resolution
const DEFAULT_QUALITY = 0.92;       // 92% Visually lossless quality
const SKIP_BELOW_BYTES = 1024 * 1024; // Skip compression if file is already <= 1 MB

export const readFileAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });

export async function compressImageFile(file, options = {}) {
  const maxDimension = options.maxDimension || DEFAULT_MAX_DIMENSION;
  const quality = options.quality || DEFAULT_QUALITY;

  const fallbackResult = async () => {
    let dataUrl = '';
    try {
      dataUrl = await readFileAsDataURL(file);
    } catch (_) {
      dataUrl = '';
    }
    return {
      file,
      previewUrl: URL.createObjectURL(file),
      dataUrl: dataUrl || URL.createObjectURL(file),
    };
  };

  if (!file || typeof file !== 'object') {
    return fallbackResult();
  }

  const mime = (file.type || '').toLowerCase();
  if (
    mime.includes('svg') ||
    mime.includes('gif') ||
    file.size <= SKIP_BELOW_BYTES
  ) {
    return fallbackResult();
  }

  try {
    const objectUrl = URL.createObjectURL(file);
    const img = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = (e) => reject(e);
      image.src = objectUrl;
    });

    let { width, height } = img;
    if (!width || !height) {
      URL.revokeObjectURL(objectUrl);
      return fallbackResult();
    }

    if (width > maxDimension || height > maxDimension) {
      if (width >= height) {
        height = Math.round((height * maxDimension) / width);
        width = maxDimension;
      } else {
        width = Math.round((width * maxDimension) / height);
        height = maxDimension;
      }
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      URL.revokeObjectURL(objectUrl);
      return fallbackResult();
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const useWebp = mime.includes('png') || mime.includes('webp');
    const outputMime = useWebp ? 'image/webp' : 'image/jpeg';

    if (!useWebp) {
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);
    }
    ctx.drawImage(img, 0, 0, width, height);
    URL.revokeObjectURL(objectUrl);

    const blob = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), outputMime, quality)
    );

    if (!blob || blob.size >= file.size) {
      return fallbackResult();
    }

    const ext = useWebp ? '.webp' : '.jpg';
    const baseName = (file.name || 'photo').replace(/\.[^/.]+$/, '');
    const optimizedFile = new File([blob], `${baseName}${ext}`, {
      type: outputMime,
      lastModified: Date.now(),
    });

    const dataUrl = await readFileAsDataURL(optimizedFile);
    const previewUrl = URL.createObjectURL(optimizedFile);

    return {
      file: optimizedFile,
      previewUrl,
      dataUrl,
    };
  } catch (err) {
    return fallbackResult();
  }
}

export async function compressImageToDataUrl(file, options = {}) {
  const res = await compressImageFile(file, options);
  return res.dataUrl;
}

